import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';

const getLocalDateString = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function Calendar() {
  const { userInfo } = useAuth();
  const { selectedProjectID } = useProject();
  
  const [meetings, setMeetings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter po agentu / korisniku (default: 'all')
  const [filterUserID, setFilterUserID] = useState('all');

  // Mjesec i godina
  const [currentDate, setCurrentDate] = useState(new Date());

  // Modal stanja
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);

  // Polja forme
  const [title, setTitle] = useState('');
  const [assignedUserID, setAssignedUserID] = useState('');
  const [customerID, setCustomerID] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [meetingDate, setMeetingDate] = useState(getLocalDateString());
  const [meetingTime, setMeetingTime] = useState('10:00');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('Zakazan');
  const [saving, setSaving] = useState(false);

  // Listener za zatvaranje modala na tipku ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  // 1. Dohvat podataka
  const fetchMeetings = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_BASE_URL}/meetingList?`;
      if (selectedProjectID) url += `projectID=${selectedProjectID}&`;
      if (filterUserID !== 'all') url += `userID=${filterUserID}&`;
      
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json().catch(() => []);
      setMeetings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Greška pri dohvaćanju sastanaka:", e);
    }
  };

  const fetchTeamAndCustomers = async () => {
    const token = localStorage.getItem('token');
    try {
      // Dohvat kupaca
      const custRes = await fetch(`${API_BASE_URL}/customerList`, { headers: { 'Authorization': `Bearer ${token}` } });
      const custData = await custRes.json().catch(() => []);
      setCustomers(Array.isArray(custData) ? custData : []);

      // Dohvat članova tima (korisnika)
      const userRes = await fetch(`${API_BASE_URL}/userList`, { headers: { 'Authorization': `Bearer ${token}` } });
      const userData = await userRes.json().catch(() => []);
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (e) {
      console.error("Greška podaci:", e);
    }
  };

  useEffect(() => {
    fetchTeamAndCustomers();
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchMeetings().finally(() => setLoading(false));
  }, [selectedProjectID, filterUserID]);

  // Otvaranje modala za novi sastanak
  const handleOpenCreate = (dateStr = null) => {
    setEditingMeeting(null);
    setTitle('');
    setAssignedUserID(String(userInfo?.userID || '')); // Automatski dodijeli ulogiranom korisniku
    setCustomerID('');
    setClientName('');
    setClientPhone('');
    setMeetingDate(dateStr || getLocalDateString());
    setMeetingTime('10:00');
    setLocation('');
    setNotes('');
    setStatus('Zakazan');
    setIsModalOpen(true);
  };

  // Otvaranje modala za uređivanje
  const handleOpenEdit = (m, e) => {
    if (e) e.stopPropagation();
    setEditingMeeting(m);
    setTitle(m.title || '');
    setAssignedUserID(m.userID ? String(m.userID) : String(userInfo?.userID || ''));
    setCustomerID(m.customerID ? String(m.customerID) : '');
    setClientName(m.clientName || '');
    setClientPhone(m.clientPhone || '');
    setMeetingDate(m.meetingDate ? String(m.meetingDate).substring(0, 10) : getLocalDateString());
    setMeetingTime(m.meetingTime || '09:00');
    setLocation(m.location || '');
    setNotes(m.notes || '');
    setStatus(m.status || 'Zakazan');
    setIsModalOpen(true);
  };

  // Spremanje sastanka
  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim() || !meetingDate) {
      alert("Naslov i datum su obavezni.");
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    let finalName = clientName;
    let finalPhone = clientPhone;
    if (customerID) {
      const selCust = customers.find(c => String(c.customerID || c.id) === String(customerID));
      if (selCust) {
        if (!finalName) finalName = selCust.fullName || selCust.fullname;
        if (!finalPhone) finalPhone = selCust.phoneNumber || selCust.phonenumber;
      }
    }

    const payload = {
      projectID: selectedProjectID || null,
      customerID: customerID ? parseInt(customerID) : null,
      userID: assignedUserID ? parseInt(assignedUserID) : null,
      title,
      clientName: finalName,
      clientPhone: finalPhone,
      meetingDate,
      meetingTime,
      location,
      notes,
      status
    };

    try {
      if (editingMeeting) {
        payload.meetingID = editingMeeting.meetingID;
        await fetch(`${API_BASE_URL}/meetingUpdateRecord`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        await fetch(`${API_BASE_URL}/meetingInsertRecord`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      await fetchMeetings();
      setIsModalOpen(false);
    } catch (err) {
      alert("Greška pri spremanju.");
    } finally {
      setSaving(false);
    }
  };

  // Brisanje sastanka
  const handleDelete = async () => {
    if (!editingMeeting) return;
    if (!window.confirm("Želite li obrisati ovaj sastanak?")) return;

    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_BASE_URL}/meetingDeleteRecord`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ meetingID: editingMeeting.meetingID })
      });
      await fetchMeetings();
      setIsModalOpen(false);
    } catch (e) {
      alert("Greška pri brisanju.");
    }
  };

  // Kalendar logika
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const startingDay = firstDay === 0 ? 6 : firstDay - 1;
  const totalDays = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const setToday = () => setCurrentDate(new Date());

  const monthNames = [
    "Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj",
    "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac"
  ];

  // Danas i sutra izračun
  const todayObj = new Date();
  const todayStr = getLocalDateString(todayObj);

  const tomorrowObj = new Date();
  tomorrowObj.setDate(tomorrowObj.getDate() + 1);
  const tomorrowStr = getLocalDateString(tomorrowObj);

  const todayMeetings = meetings.filter(m => m.meetingDate && String(m.meetingDate).substring(0, 10) === todayStr);
  const tomorrowMeetings = meetings.filter(m => m.meetingDate && String(m.meetingDate).substring(0, 10) === tomorrowStr);

  return (
    <div className="space-y-6">
      
      {/* 1. ZAGLAVLJE KALENDARA S FILTEROM AGENTA */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
        
        {/* Lijevi dio: Mjesec i navigacija */}
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-slate-800">
            {monthNames[month]} <span className="text-slate-400 font-semibold">{year}</span>
          </h1>

          <div className="flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200">
            <button 
              type="button" 
              onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-600 hover:bg-white transition font-bold text-base cursor-pointer"
            >
              ‹
            </button>
            <button 
              type="button" 
              onClick={setToday}
              className="px-3 h-8 flex items-center justify-center rounded-lg text-xs font-bold text-slate-700 hover:bg-white transition cursor-pointer"
            >
              Danas
            </button>
            <button 
              type="button" 
              onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-600 hover:bg-white transition font-bold text-base cursor-pointer"
            >
              ›
            </button>
          </div>
        </div>

        {/* Desni dio: Filter agenata + Novi sastanak */}
        <div className="flex flex-wrap items-center gap-3">
          
          {/* FILTER: MOJI SASTANCI / CIJELI TIM */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold">
            <button
              type="button"
              onClick={() => setFilterUserID('all')}
              className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${filterUserID === 'all' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Cijeli tim
            </button>
            {userInfo?.userID && (
              <button
                type="button"
                onClick={() => setFilterUserID(String(userInfo.userID))}
                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${filterUserID === String(userInfo.userID) ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}
              >
                Moji sastanci
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => handleOpenCreate()}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition flex items-center gap-1.5 shadow-xs cursor-pointer"
          >
            <span className="text-base leading-none">+</span> Novi sastanak
          </button>
        </div>

      </div>

      {/* 2. GLAVNI GRID: KALENDAR LIJEVO + DANAS/SUTRA DESNO */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* LIJEVI DIO: VELIKI KALENDAR (8 STUPACA) */}
        <div className="lg:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200 text-center py-2.5 text-xs font-bold uppercase text-slate-500">
            <div>Ponedjeljak</div>
            <div>Utorak</div>
            <div>Srijeda</div>
            <div>Četvrtak</div>
            <div>Petak</div>
            <div className="text-slate-400">Subota</div>
            <div className="text-slate-400">Nedjelja</div>
          </div>

          <div className="grid grid-cols-7 divide-x divide-y divide-slate-100 bg-slate-50/20">
            {Array.from({ length: startingDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[105px] bg-slate-50/40"></div>
            ))}

            {Array.from({ length: totalDays }).map((_, i) => {
              const dayNum = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
              const isToday = todayStr === dateStr;
              const dayMeetings = meetings.filter(m => m.meetingDate && String(m.meetingDate).substring(0, 10) === dateStr);

              return (
                <div
                  key={dayNum}
                  onClick={() => handleOpenCreate(dateStr)}
                  className="min-h-[105px] p-2 flex flex-col justify-between hover:bg-slate-50/80 transition cursor-pointer group bg-white"
                >
                  <div className="flex items-center justify-between">
                    <span 
                      className={`text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center ${
                        isToday 
                          ? 'bg-slate-900 text-white shadow-xs' 
                          : 'text-slate-700 group-hover:text-indigo-600'
                      }`}
                    >
                      {dayNum}
                    </span>

                    <span className="text-[10px] font-bold text-slate-300 group-hover:text-slate-400 opacity-0 group-hover:opacity-100 transition">
                      + Dodaj
                    </span>
                  </div>

                  <div className="space-y-1 mt-1 overflow-y-auto max-h-[70px]">
                    {dayMeetings.map((m) => (
                      <div
                        key={m.meetingID}
                        onClick={(e) => handleOpenEdit(m, e)}
                        className={`px-2 py-1 rounded-lg text-[11px] font-semibold border flex flex-col gap-0.5 transition ${
                          m.status === 'Odrađen'
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : m.status === 'Otkazan'
                            ? 'bg-slate-100 text-slate-400 border-slate-200 line-through'
                            : 'bg-indigo-50 text-indigo-900 border-indigo-100 hover:bg-indigo-100'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-bold shrink-0">{m.meetingTime}</span>
                          <span className="truncate flex-1">{m.title}</span>
                          {m.status === 'Odrađen' && <span className="text-[9px] font-bold">✓</span>}
                        </div>
                        {m.assignedUserName && (
                          <span className="text-[9px] text-slate-500 font-normal truncate">
                            👤 {m.assignedUserName}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* DESNI DIO: PANEL DANAS & SUTRA (4 STUPCA) */}
        <div className="lg:col-span-4 space-y-4">
          
          {/* KARTICA: DANAS */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-pulse"></span>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  Danas <span className="text-slate-400 font-normal">({todayObj.toLocaleDateString('hr-HR', { day: 'numeric', month: 'numeric' })})</span>
                </h2>
              </div>
              <button
                type="button"
                onClick={() => handleOpenCreate(todayStr)}
                className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer"
              >
                + Dodaj
              </button>
            </div>

            {todayMeetings.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">Nema zakazanih sastanaka za danas.</p>
            ) : (
              <div className="space-y-2">
                {todayMeetings.map((m) => (
                  <div
                    key={m.meetingID}
                    onClick={(e) => handleOpenEdit(m, e)}
                    className="p-3 bg-slate-50 hover:bg-slate-100 border border-slate-100 hover:border-slate-200 rounded-xl flex items-center justify-between gap-2 transition cursor-pointer"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="bg-slate-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0">
                          {m.meetingTime}
                        </span>
                        <p className="text-xs font-bold text-slate-800 truncate">{m.title}</p>
                      </div>
                      
                      {(m.clientName || m.customerFullName) && (
                        <p className="text-[11px] text-slate-600 truncate mt-1">
                          Kupac: {m.clientName || m.customerFullName}
                        </p>
                      )}

                      {m.assignedUserName && (
                        <p className="text-[10px] font-bold text-indigo-600 truncate mt-0.5">
                          Voditelj: {m.assignedUserName}
                        </p>
                      )}
                    </div>

                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0 ${
                      m.status === 'Odrađen' ? 'bg-emerald-100 text-emerald-800' : 'bg-white text-slate-600 border border-slate-200'
                    }`}>
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* KARTICA: SUTRA */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-300"></span>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  Sutra <span className="text-slate-400 font-normal">({tomorrowObj.toLocaleDateString('hr-HR', { day: 'numeric', month: 'numeric' })})</span>
                </h2>
              </div>
              <button
                type="button"
                onClick={() => handleOpenCreate(tomorrowStr)}
                className="text-[11px] font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                + Dodaj
              </button>
            </div>

            {tomorrowMeetings.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">Nema zakazanih sastanaka za sutra.</p>
            ) : (
              <div className="space-y-2">
                {tomorrowMeetings.map((m) => (
                  <div
                    key={m.meetingID}
                    onClick={(e) => handleOpenEdit(m, e)}
                    className="p-3 bg-slate-50 hover:bg-slate-100 border border-slate-100 hover:border-slate-200 rounded-xl flex items-center justify-between gap-2 transition cursor-pointer"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0">
                          {m.meetingTime}
                        </span>
                        <p className="text-xs font-bold text-slate-800 truncate">{m.title}</p>
                      </div>
                      
                      {(m.clientName || m.customerFullName) && (
                        <p className="text-[11px] text-slate-500 truncate mt-1">
                          Kupac: {m.clientName || m.customerFullName}
                        </p>
                      )}

                      {m.assignedUserName && (
                        <p className="text-[10px] font-bold text-indigo-600 truncate mt-0.5">
                          Voditelj: {m.assignedUserName}
                        </p>
                      )}
                    </div>

                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0 ${
                      m.status === 'Odrađen' ? 'bg-emerald-100 text-emerald-800' : 'bg-white text-slate-600 border border-slate-200'
                    }`}>
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </div>

      {/* 3. MODAL S POLJEM ZA DODJELU KORISNIKA / VODITELJA */}
      {isModalOpen && (
        <div 
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[999] flex items-center justify-center p-4 cursor-pointer"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100 space-y-4 cursor-default"
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-800">
                {editingMeeting ? "Uredi sastanak" : "Novi sastanak"}
              </h3>
              <button 
                type="button" 
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 text-xs cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Svrha / Naslov (*)</label>
                <input
                  type="text"
                  required
                  placeholder="pr. Razgledavanje stana A50"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                />
              </div>

              {/* TKO VODI SASTANAK (DODJELA ČLANU TIMA) */}
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Voditelj sastanka / Dodijeljeno (*)</label>
                <select
                  value={assignedUserID}
                  onChange={(e) => setAssignedUserID(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer"
                >
                  <option value="">-- Odaberi člana tima --</option>
                  {users.map(u => (
                    <option key={u.userID || u.id} value={u.userID || u.id}>
                      {u.fullName || u.fullname || u.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Kupac iz CRM-a (opcionalno)</label>
                <select
                  value={customerID}
                  onChange={(e) => {
                    setCustomerID(e.target.value);
                    const sel = customers.find(c => String(c.customerID || c.id) === e.target.value);
                    if (sel) {
                      setClientName(sel.fullName || sel.fullname || '');
                      setClientPhone(sel.phoneNumber || sel.phonenumber || '');
                    }
                  }}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer"
                >
                  <option value="">-- Slobodan unos ili odaberi kupca --</option>
                  {customers.map(c => (
                    <option key={c.customerID || c.id} value={c.customerID || c.id}>
                      {c.fullName || c.fullname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Ime klijenta</label>
                  <input
                    type="text"
                    placeholder="Ime i prezime"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Broj mobitela</label>
                  <input
                    type="text"
                    placeholder="091/..."
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Datum (*)</label>
                  <input
                    type="date"
                    required
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 cursor-pointer focus:bg-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Vrijeme</label>
                  <input
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 cursor-pointer focus:bg-white focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Lokacija / Bilješka</label>
                  <input
                    type="text"
                    placeholder="npr. Gradilište Brda"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none cursor-pointer"
                  >
                    <option value="Zakazan">Zakazan</option>
                    <option value="Odrađen">Odrađen</option>
                    <option value="Otkazan">Otkazan</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t border-slate-100">
                {editingMeeting && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold text-xs px-3 py-2.5 rounded-xl transition cursor-pointer"
                  >
                    Obriši
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs py-2.5 rounded-xl transition cursor-pointer"
                >
                  Odustani
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs py-2.5 rounded-xl transition shadow-xs disabled:opacity-50 cursor-pointer"
                >
                  {saving ? 'Spremanje...' : (editingMeeting ? 'Spremi' : 'Zakaži')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}